import React, { useState, useEffect } from "react";
import { useAuth, useLanguage, API } from "../App";
import AdminLayout from "../components/AdminLayout";
import { FeatureGate } from "../components/FeatureGate";
import { Button } from "../components/ui/button";
import { 
  TrendingUp, 
  TrendingDown,
  Calendar,
  Euro,
  Users,
  Briefcase,
  BarChart3,
  PieChart,
  ArrowUp,
  ArrowDown,
  Loader2,
  Download,
  Wallet,
  AlertTriangle,
  Sparkles,
  RefreshCw,
  Crown,
  Target
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const SEGMENT_COLORS = {
  new: { bg: "#94A3B8", label_it: "Nuovo", label_en: "New" },
  low: { bg: "#EF4444", label_it: "Basso Valore", label_en: "Low Value" },
  medium: { bg: "#F97316", label_it: "Medio", label_en: "Medium" },
  high: { bg: "#22C55E", label_it: "Alto Valore", label_en: "High Value" },
  at_risk: { bg: "#8B5CF6", label_it: "A Rischio", label_en: "At Risk" },
  vip: { bg: "#FFD700", label_it: "VIP", label_en: "VIP" },
};

const Analytics = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(6);
  const [ltvData, setLtvData] = useState(null);
  const [ltvLoading, setLtvLoading] = useState(false);
  const [recalculating, setRecalculating] = useState(false);

  useEffect(() => {
    fetchAnalytics();
    fetchLTVDashboard();
  }, [selectedPeriod]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/analytics/monthly-trends`, {
        params: { months: selectedPeriod },
        withCredentials: true
      });
      setData(response.data);
    } catch (error) {
      toast.error(language === "it" ? "Errore nel caricamento" : "Loading error");
    } finally {
      setLoading(false);
    }
  };

  const fetchLTVDashboard = async () => {
    setLtvLoading(true);
    try {
      const response = await axios.get(`${API}/ltv/dashboard`, {
        withCredentials: true
      });
      setLtvData(response.data);
    } catch (error) {
      console.error("LTV dashboard error:", error);
    } finally {
      setLtvLoading(false);
    }
  };

  const handleRecalculateLTV = async () => {
    setRecalculating(true);
    try {
      await axios.post(`${API}/ltv/recalculate-all`, {}, { withCredentials: true });
      toast.success(language === "it" ? "LTV ricalcolato con successo" : "LTV recalculated successfully");
      fetchLTVDashboard();
    } catch (error) {
      toast.error(language === "it" ? "Errore nel ricalcolo" : "Recalculation error");
    } finally {
      setRecalculating(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatMonth = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const months = language === "it" 
      ? ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic']
      : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[parseInt(month) - 1]} ${year}`;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
        </div>
      </AdminLayout>
    );
  }

  const maxRevenue = data?.monthly_data ? Math.max(...data.monthly_data.map(m => m.revenue), 1) : 1;
  const maxAppointments = data?.monthly_data ? Math.max(...data.monthly_data.map(m => m.appointments), 1) : 1;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <BarChart3 className="w-7 h-7 text-violet-500" />
              {language === "it" ? "Analytics" : "Analytics"}
            </h1>
            <p className="text-muted-foreground mt-1">
              {language === "it" 
                ? "Monitora l'andamento della tua attività" 
                : "Monitor your salon's performance"}
            </p>
          </div>
          
          {/* Period Selector */}
          <div className="flex gap-2">
            {[3, 6, 12].map(months => (
              <Button
                key={months}
                variant={selectedPeriod === months ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedPeriod(months)}
                className="rounded-full"
              >
                {months} {language === "it" ? "mesi" : "months"}
              </Button>
            ))}
            
            {/* Export Buttons */}
            <div className="flex gap-2 ml-4 border-l pl-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(`${API}/analytics/export/appointments`, '_blank')}
                title={language === "it" ? "Esporta appuntamenti" : "Export appointments"}
              >
                <Download className="w-4 h-4 mr-1" />
                {language === "it" ? "Appuntamenti" : "Appointments"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(`${API}/analytics/export/revenue`, '_blank')}
                title={language === "it" ? "Esporta incassi" : "Export revenue"}
              >
                <Download className="w-4 h-4 mr-1" />
                {language === "it" ? "Incassi" : "Revenue"}
              </Button>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Revenue Card */}
          <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-5 text-white">
            <div className="flex items-center justify-between mb-3">
              <Euro className="w-8 h-8 opacity-80" />
              {data?.summary?.revenue_growth !== 0 && (
                <div className={`flex items-center gap-1 text-sm ${data?.summary?.revenue_growth > 0 ? 'text-emerald-100' : 'text-red-200'}`}>
                  {data?.summary?.revenue_growth > 0 ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
                  {Math.abs(data?.summary?.revenue_growth || 0)}%
                </div>
              )}
            </div>
            <p className="text-emerald-100 text-sm">{language === "it" ? "Incassi Totali" : "Total Revenue"}</p>
            <p className="text-3xl font-bold mt-1">{formatCurrency(data?.summary?.total_revenue || 0)}</p>
          </div>

          {/* Appointments Card */}
          <div className="bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl p-5 text-white">
            <div className="flex items-center justify-between mb-3">
              <Calendar className="w-8 h-8 opacity-80" />
              {data?.summary?.appointments_growth !== 0 && (
                <div className={`flex items-center gap-1 text-sm ${data?.summary?.appointments_growth > 0 ? 'text-violet-100' : 'text-red-200'}`}>
                  {data?.summary?.appointments_growth > 0 ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
                  {Math.abs(data?.summary?.appointments_growth || 0)}%
                </div>
              )}
            </div>
            <p className="text-violet-100 text-sm">{language === "it" ? "Appuntamenti" : "Appointments"}</p>
            <p className="text-3xl font-bold mt-1">{data?.summary?.total_appointments || 0}</p>
          </div>

          {/* Completed Card */}
          <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl p-5 text-white">
            <div className="flex items-center justify-between mb-3">
              <TrendingUp className="w-8 h-8 opacity-80" />
            </div>
            <p className="text-blue-100 text-sm">{language === "it" ? "Completati" : "Completed"}</p>
            <p className="text-3xl font-bold mt-1">{data?.summary?.total_completed || 0}</p>
          </div>

          {/* Cancelled Card */}
          <div className="bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl p-5 text-white">
            <div className="flex items-center justify-between mb-3">
              <TrendingDown className="w-8 h-8 opacity-80" />
            </div>
            <p className="text-orange-100 text-sm">{language === "it" ? "Cancellati" : "Cancelled"}</p>
            <p className="text-3xl font-bold mt-1">{data?.summary?.total_cancelled || 0}</p>
          </div>
        </div>

        {/* Charts Section */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Monthly Revenue Chart */}
          <div className="bg-card rounded-2xl border p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Euro className="w-5 h-5 text-emerald-500" />
              {language === "it" ? "Incassi Mensili" : "Monthly Revenue"}
            </h2>
            <div className="space-y-3">
              {data?.monthly_data?.map((month, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <span className="text-sm text-muted-foreground w-20">{formatMonth(month.month)}</span>
                  <div className="flex-1 h-8 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full transition-all duration-500 flex items-center justify-end pr-3"
                      style={{ width: `${(month.revenue / maxRevenue) * 100}%`, minWidth: month.revenue > 0 ? '60px' : '0' }}
                    >
                      {month.revenue > 0 && (
                        <span className="text-xs font-medium text-white">{formatCurrency(month.revenue)}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Monthly Appointments Chart */}
          <div className="bg-card rounded-2xl border p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-violet-500" />
              {language === "it" ? "Appuntamenti Mensili" : "Monthly Appointments"}
            </h2>
            <div className="space-y-3">
              {data?.monthly_data?.map((month, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <span className="text-sm text-muted-foreground w-20">{formatMonth(month.month)}</span>
                  <div className="flex-1 h-8 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-violet-500 to-purple-500 rounded-full transition-all duration-500 flex items-center justify-end pr-3"
                      style={{ width: `${(month.appointments / maxAppointments) * 100}%`, minWidth: month.appointments > 0 ? '40px' : '0' }}
                    >
                      {month.appointments > 0 && (
                        <span className="text-xs font-medium text-white">{month.appointments}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Top Services */}
        <div className="bg-card rounded-2xl border p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-pink-500" />
            {language === "it" ? "Servizi Più Richiesti" : "Top Services"}
          </h2>
          
          {data?.top_services?.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.top_services.map((service, idx) => (
                <div 
                  key={idx} 
                  className="flex items-center gap-4 p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50"
                >
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                    style={{
                      background: idx === 0 ? 'linear-gradient(135deg, #f59e0b, #f97316)' :
                                 idx === 1 ? 'linear-gradient(135deg, #94a3b8, #64748b)' :
                                 idx === 2 ? 'linear-gradient(135deg, #d97706, #b45309)' :
                                 'linear-gradient(135deg, #6366f1, #8b5cf6)'
                    }}
                  >
                    {idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 dark:text-white truncate">{service.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {service.count} {language === "it" ? "prenotazioni" : "bookings"} • {formatCurrency(service.revenue)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">
              {language === "it" ? "Nessun dato disponibile" : "No data available"}
            </p>
          )}
        </div>

        {/* Monthly Details Table */}
        <div className="bg-card rounded-2xl border p-6 overflow-x-auto">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <PieChart className="w-5 h-5 text-blue-500" />
            {language === "it" ? "Dettaglio Mensile" : "Monthly Details"}
          </h2>
          
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">
                  {language === "it" ? "Mese" : "Month"}
                </th>
                <th className="text-right py-3 px-2 text-sm font-medium text-muted-foreground">
                  {language === "it" ? "Appuntamenti" : "Appointments"}
                </th>
                <th className="text-right py-3 px-2 text-sm font-medium text-muted-foreground">
                  {language === "it" ? "Completati" : "Completed"}
                </th>
                <th className="text-right py-3 px-2 text-sm font-medium text-muted-foreground">
                  {language === "it" ? "Incasso" : "Revenue"}
                </th>
                <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">
                  {language === "it" ? "Top Servizio" : "Top Service"}
                </th>
              </tr>
            </thead>
            <tbody>
              {data?.monthly_data?.map((month, idx) => (
                <tr key={idx} className="border-b last:border-0 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  <td className="py-3 px-2 font-medium">{formatMonth(month.month)}</td>
                  <td className="py-3 px-2 text-right">{month.appointments}</td>
                  <td className="py-3 px-2 text-right text-emerald-600">{month.completed}</td>
                  <td className="py-3 px-2 text-right font-medium text-emerald-600">{formatCurrency(month.revenue)}</td>
                  <td className="py-3 px-2">
                    {month.top_services?.[0] && (
                      <span className="text-sm text-muted-foreground">
                        {month.top_services[0].name} ({month.top_services[0].count})
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ========== LTV ANALYTICS SECTION ========== */}
        <FeatureGate feature="ltv_analytics">
        <div className="space-y-6" data-testid="ltv-analytics-section">
          {/* LTV Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pt-4 border-t border-slate-200 dark:border-slate-700">
            <div>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <Wallet className="w-6 h-6 text-violet-500" />
                {language === "it" ? "Valore Clienti (LTV)" : "Customer Value (LTV)"}
              </h2>
              <p className="text-muted-foreground text-sm mt-1">
                {language === "it" 
                  ? "Analisi del valore e segmentazione automatica dei clienti" 
                  : "Customer value analysis and automatic segmentation"}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRecalculateLTV}
              disabled={recalculating}
              className="rounded-full"
              data-testid="recalculate-ltv-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${recalculating ? "animate-spin" : ""}`} />
              {language === "it" ? "Ricalcola LTV" : "Recalculate LTV"}
            </Button>
          </div>

          {ltvLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
            </div>
          ) : ltvData ? (
            <>
              {/* LTV Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl p-5 text-white" data-testid="ltv-total-value">
                  <Wallet className="w-8 h-8 opacity-80 mb-3" />
                  <p className="text-violet-100 text-sm">{language === "it" ? "Valore Totale Clienti" : "Total Client Value"}</p>
                  <p className="text-3xl font-bold mt-1">{formatCurrency(ltvData.total_ltv)}</p>
                </div>
                <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-5 text-white" data-testid="ltv-average">
                  <Target className="w-8 h-8 opacity-80 mb-3" />
                  <p className="text-emerald-100 text-sm">{language === "it" ? "LTV Medio" : "Average LTV"}</p>
                  <p className="text-3xl font-bold mt-1">{formatCurrency(ltvData.average_ltv)}</p>
                </div>
                <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl p-5 text-white" data-testid="ltv-clients-count">
                  <Users className="w-8 h-8 opacity-80 mb-3" />
                  <p className="text-blue-100 text-sm">{language === "it" ? "Clienti con Storico" : "Clients with History"}</p>
                  <p className="text-3xl font-bold mt-1">{ltvData.clients_with_history} / {ltvData.total_clients}</p>
                </div>
                <div className="bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl p-5 text-white" data-testid="ltv-at-risk-count">
                  <AlertTriangle className="w-8 h-8 opacity-80 mb-3" />
                  <p className="text-orange-100 text-sm">{language === "it" ? "Clienti a Rischio" : "At Risk Clients"}</p>
                  <p className="text-3xl font-bold mt-1">{ltvData.at_risk_count}</p>
                </div>
              </div>

              {/* Segment Distribution */}
              <div className="bg-card rounded-2xl border p-6" data-testid="ltv-segment-distribution">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-violet-500" />
                  {language === "it" ? "Distribuzione Segmenti" : "Segment Distribution"}
                </h3>
                {ltvData.segment_distribution?.length > 0 ? (
                  <div className="space-y-3">
                    {ltvData.segment_distribution.map((seg) => {
                      const segInfo = SEGMENT_COLORS[seg.segment] || SEGMENT_COLORS.new;
                      const maxCount = Math.max(...ltvData.segment_distribution.map(s => s.count), 1);
                      return (
                        <div key={seg.segment} className="flex items-center gap-3" data-testid={`ltv-segment-row-${seg.segment}`}>
                          <div className="w-28 flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: segInfo.bg }} />
                            <span className="text-sm font-medium truncate">
                              {language === "it" ? segInfo.label_it : segInfo.label_en}
                            </span>
                          </div>
                          <div className="flex-1 h-8 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                            <div 
                              className="h-full rounded-full transition-all duration-500 flex items-center justify-end pr-3"
                              style={{ 
                                width: `${Math.max((seg.count / maxCount) * 100, seg.count > 0 ? 15 : 0)}%`,
                                backgroundColor: segInfo.bg 
                              }}
                            >
                              {seg.count > 0 && (
                                <span className="text-xs font-bold text-white">{seg.count}</span>
                              )}
                            </div>
                          </div>
                          <span className="text-sm text-muted-foreground w-14 text-right">{seg.percentage}%</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">
                    {language === "it" ? "Nessun dato disponibile" : "No data available"}
                  </p>
                )}
              </div>

              <div className="grid lg:grid-cols-2 gap-6">
                {/* Top Clients */}
                <div className="bg-card rounded-2xl border p-6" data-testid="ltv-top-clients">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Crown className="w-5 h-5 text-amber-500" />
                    {language === "it" ? "Clienti Top per Valore" : "Top Clients by Value"}
                  </h3>
                  {ltvData.top_clients?.length > 0 ? (
                    <div className="space-y-2">
                      {ltvData.top_clients.slice(0, 8).map((client, idx) => {
                        const segInfo = SEGMENT_COLORS[client.segment] || SEGMENT_COLORS.new;
                        return (
                          <div key={client.client_id} className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                            <div 
                              className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                              style={{
                                background: idx === 0 ? 'linear-gradient(135deg, #f59e0b, #f97316)' :
                                           idx === 1 ? 'linear-gradient(135deg, #94a3b8, #64748b)' :
                                           idx === 2 ? 'linear-gradient(135deg, #d97706, #b45309)' :
                                           'linear-gradient(135deg, #6366f1, #8b5cf6)'
                              }}
                            >
                              {idx + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{client.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {client.visit_count} {language === "it" ? "visite" : "visits"}
                              </p>
                            </div>
                            <span 
                              className="px-2 py-0.5 rounded-full text-xs font-semibold text-white"
                              style={{ backgroundColor: segInfo.bg }}
                            >
                              {language === "it" ? segInfo.label_it : segInfo.label_en}
                            </span>
                            <span className="font-bold text-sm text-emerald-600 dark:text-emerald-400 whitespace-nowrap">
                              {formatCurrency(client.total_spent)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-center text-muted-foreground py-8 text-sm">
                      {language === "it" ? "Nessun dato" : "No data"}
                    </p>
                  )}
                </div>

                {/* At Risk Clients */}
                <div className="bg-card rounded-2xl border p-6" data-testid="ltv-at-risk-clients">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-500" />
                    {language === "it" ? "Clienti a Rischio Abbandono" : "At Risk Clients"}
                  </h3>
                  {ltvData.at_risk_clients?.length > 0 ? (
                    <div className="space-y-2">
                      {ltvData.at_risk_clients.slice(0, 8).map((client) => (
                        <div key={client.client_id} className="flex items-center gap-3 p-3 rounded-xl bg-red-50/50 dark:bg-red-950/10 border border-red-100 dark:border-red-900/30">
                          <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                            <AlertTriangle className="w-4 h-4 text-red-500" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{client.name}</p>
                            <p className="text-xs text-red-600 dark:text-red-400">
                              {client.days_since_last_visit} {language === "it" ? "giorni fa" : "days ago"}
                              {client.last_visit && ` - ${client.last_visit}`}
                            </p>
                          </div>
                          <span className="font-bold text-sm whitespace-nowrap">
                            {formatCurrency(client.total_spent)}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Sparkles className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">
                        {language === "it" ? "Nessun cliente a rischio!" : "No at-risk clients!"}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              {language === "it" ? "Nessun dato LTV disponibile" : "No LTV data available"}
            </div>
          )}
        </div>
        </FeatureGate>
      </div>
    </AdminLayout>
  );
};

export default Analytics;
